import { useEffect, useState } from "react";
import { ArrowUp, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const TIER_COLORS = { bronze: "#B87333", gold: "#FFD700", silver: "#C0C0C0" };
const ORDER = ["bronze", "gold", "silver"];

export const UpgradeDialog = ({ open, onOpenChange, status, onUpgraded }) => {
  const [busy, setBusy] = useState(null);
  if (!status) return null;
  const currentIdx = ORDER.indexOf(status.plan);
  const options = ORDER
    .map((id) => status.plans?.find((p) => p.id === id))
    .filter((p) => p && p.id !== status.plan);

  const upgrade = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/platform/billing/upgrade", { plan: planId });
      toast.success(`You're now on the ${data.label} plan`);
      onOpenChange(false);
      onUpgraded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Plan change failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="upgrade-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Change your plan</DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            Switches instantly — Stripe prorates the difference on your next bill.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {options.map((p) => {
            const color = TIER_COLORS[p.id];
            const isUp = ORDER.indexOf(p.id) > currentIdx;
            return (
              <button
                key={p.id}
                onClick={() => upgrade(p.id)}
                disabled={busy !== null}
                className="w-full flex items-center justify-between gap-3 border border-white/10 bg-zinc-900/50 hover:bg-zinc-900 px-4 py-3 text-left transition-colors disabled:opacity-50"
                data-testid={`upgrade-option-${p.id}`}
              >
                <div>
                  <div className="tracker text-[11px] font-bold" style={{ color }}>{p.label.toUpperCase()}</div>
                  <div className="text-[11px] text-zinc-500 flex items-center gap-1"><Lightning size={10} weight="fill" style={{ color }} /> {p.limit}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-display text-lg">£{p.amount / 100}<span className="tracker text-zinc-500 text-[9px]">/mo</span></span>
                  {isUp && <ArrowUp size={14} weight="bold" className="text-emerald-400" />}
                </div>
                {busy === p.id && <span className="tracker text-[9px] text-zinc-400">Switching…</span>}
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Small hook: fetch billing status + expose upgrade dialog controls
export const useUpgradeDialog = () => {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/platform/billing/status");
      setStatus(data);
      return data;
    } catch {
      return null;
    }
  };
  useEffect(() => { load(); }, []);

  return { status, reload: load, open, setOpen };
};
